/**
 * Звук боя — синтезом через WebAudio, без единого файла.
 *
 * Причина не в экономии: браузер-сорс в OBS должен подниматься с пустого кэша
 * мгновенно, а любой внешний ассет — это ещё одна вещь, которая может не
 * загрузиться посреди эфира.
 *
 * Главная сложность здесь — не синтез, а КОЛИЧЕСТВО. При двухстах бойцах с
 * интервалом 700 мс на поле около трёхсот выстрелов в секунду. Проигрывать их
 * по одному нельзя: это и мгновенная перегрузка звукового движка, и на слух
 * получается белый шум вместо стрельбы. Поэтому выстрелы сливаются в залпы:
 * не чаще одного звука в 55 мс, а громкость и тембр зависят от того, сколько
 * стволов сработало за это время.
 */

import type { AnnounceId } from '../sim/state.js';

import firstBloodUrl from '../assets/sfx/first_blood.mp3';
import doubleUrl from '../assets/sfx/double.mp3';
import multiUrl from '../assets/sfx/multi.mp3';
import megaUrl from '../assets/sfx/mega.mp3';
import ultraUrl from '../assets/sfx/ultra.mp3';
import monsterUrl from '../assets/sfx/monster.mp3';
import rampageUrl from '../assets/sfx/rampage.mp3';
import dominatingUrl from '../assets/sfx/dominating.mp3';
import headshotUrl from '../assets/sfx/headshot.mp3';
import rocketUrl from '../assets/sfx/rocket.mp3';
import laserUrl from '../assets/sfx/laser.mp3';
import blasterUrl from '../assets/sfx/blaster.mp3';

/** Голоса диктора и звуки оружия — единственные файлы в проекте. */
const CLIPS = {
  first_blood: firstBloodUrl,
  double: doubleUrl,
  multi: multiUrl,
  mega: megaUrl,
  ultra: ultraUrl,
  monster: monsterUrl,
  rampage: rampageUrl,
  dominating: dominatingUrl,
  headshot: headshotUrl,
  rocket: rocketUrl,
  laser: laserUrl,
  blaster: blasterUrl,
} as const;

type ClipId = keyof typeof CLIPS;

export interface SoundOptions {
  enabled?: boolean;
  volume?: number;
}

export class Sound {
  enabled: boolean;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private volume: number;

  /** Накопитель выстрелов между озвученными залпами. */
  private pendingShots = 0;
  private lastShotAt = 0;
  private lastBoomAt = 0;

  private readonly clips = new Map<ClipId, AudioBuffer>();
  private clipsLoading = false;
  /** Когда освободится диктор — реплики не должны накладываться. */
  private announcerFreeAt = 0;
  private lastWeaponAt = 0;
  private lastBlasterAt = 0;

  constructor(opts: SoundOptions = {}) {
    this.enabled = opts.enabled ?? true;
    this.volume = opts.volume ?? 0.5;
  }

  /**
   * Готовит контекст. Возвращает true, если узлы можно создавать.
   *
   * Важно НЕ отказываться от воспроизведения, пока контекст в состоянии
   * suspended: браузер блокирует звук до первого действия пользователя, но
   * узлы создавать уже можно, а resume() дожимается тут же. Ранняя версия
   * возвращала false и молчала навсегда, если гест не поймался.
   */
  private ensure(): boolean {
    if (!this.enabled) return false;
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return true;
    }

    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return false;

    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.enabled ? this.volume : 0;
    this.master.connect(this.ctx.destination);

    // Полсекунды белого шума — основа для выстрелов и взрывов.
    const len = Math.floor(this.ctx.sampleRate * 0.5);
    this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;

    void this.ctx.resume();
    void this.loadClips();
    return true;
  }

  /**
   * Файлы декодируются один раз при первом обращении к звуку.
   *
   * Пока они грузятся, синтезированные выстрелы и взрывы уже играют — эфир
   * не должен ждать загрузки, чтобы начать звучать.
   */
  private async loadClips(): Promise<void> {
    if (this.clipsLoading || !this.ctx) return;
    this.clipsLoading = true;
    await Promise.all(
      (Object.keys(CLIPS) as ClipId[]).map(async (id) => {
        try {
          const res = await fetch(CLIPS[id]);
          const raw = await res.arrayBuffer();
          const buf = await this.ctx!.decodeAudioData(raw);
          this.clips.set(id, buf);
        } catch (e) {
          console.warn('[rekrut] не удалось загрузить звук', id, e);
        }
      }),
    );
  }

  /** Проиграть готовый файл. */
  private play(id: ClipId, volume = 1): void {
    if (!this.ensure() || !this.ctx || !this.master) return;
    const buf = this.clips.get(id);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    g.gain.value = volume;
    src.connect(g).connect(this.master);
    src.start();
  }

  /**
   * Голос диктора. Реплики не накладываются: пока звучит одна, следующие
   * отбрасываются. Наложенные друг на друга «Monster kill» превращаются в
   * кашу и обесценивают событие.
   */
  announce(id: AnnounceId, now: number, minGapMs: number): boolean {
    if (now < this.announcerFreeAt) return false;
    const buf = this.clips.get(id);
    this.announcerFreeAt = now + (buf ? buf.duration * 1000 : 0) + minGapMs;
    this.play(id, 0.9);
    return true;
  }

  /** Волна очереди из розы: выстрел бластера. */
  blaster(now: number): void {
    if (now - this.lastBlasterAt < 90) return;
    this.lastBlasterAt = now;
    this.play('blaster', 0.8);
  }

  /** Пуск ракет и лазер — свои файлы, с защитой от частого повтора. */
  weapon(kind: 'rocket' | 'laser' | 'none', now: number): void {
    // Залп розы звучит собственной очередью через счётчик выстрелов,
    // отдельный файл ему не нужен.
    if (kind === 'none') return;
    if (now - this.lastWeaponAt < 90) return;
    this.lastWeaponAt = now;
    this.play(kind, 0.75);
  }

  /** Состояние для диагностики в дев-панели. */
  state(): string {
    if (!this.enabled) return 'выключен';
    if (!this.ctx) return 'не создан';
    return this.ctx.state;
  }

  /** Вызывается по первому клику или нажатию клавиши. */
  resume(): void {
    this.ensure();
    void this.ctx?.resume();
  }

  get running(): boolean {
    return this.ctx?.state === 'running';
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.volume;
  }

  getVolume(): number {
    return this.volume;
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    if (this.master) this.master.gain.value = this.enabled ? this.volume : 0;
    return this.enabled;
  }

  // ─── события боя ─────────────────────────────────────────────────────────

  /** Накопить выстрелы; реальный звук выдаст следующий tick. */
  shots(n: number): void {
    this.pendingShots += n;
  }

  /**
   * Слить накопленные выстрелы в один залп. Чем больше стволов сработало,
   * тем ниже и жёстче звук — редкая перестрелка звучит сухими щелчками,
   * плотный бой сплошным треском.
   */
  tick(now: number): void {
    if (this.pendingShots <= 0) return;
    if (now - this.lastShotAt < 55) return;
    const n = this.pendingShots;
    this.pendingShots = 0;
    this.lastShotAt = now;
    if (!this.ensure() || !this.ctx || !this.noise || !this.master) return;

    const dense = Math.min(1, n / 14);
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = 1.3 - dense * 0.4 + Math.random() * 0.2;

    // Узкая полоса на 2.6 кГц срезала почти всю энергию, и залпы тонули под
    // взрывами. Ниже центр и шире полоса — щелчок стал слышен.
    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1500 - dense * 700;
    band.Q.value = 0.55;

    const gain = this.ctx.createGain();
    const peak = (0.22 + dense * 0.3) * (0.85 + Math.random() * 0.3);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05 + dense * 0.05);

    src.connect(band).connect(gain).connect(this.master);
    src.start(t);
    src.stop(t + 0.16);
  }

  /** Взрыв ракеты или удар по площади. */
  boom(now: number, power = 1): void {
    if (now - this.lastBoomAt < 70) return;
    this.lastBoomAt = now;
    if (!this.ensure() || !this.ctx || !this.noise || !this.master) return;
    const t = this.ctx.currentTime;

    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.playbackRate.value = 0.55 + Math.random() * 0.15;
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2200, t);
    lp.frequency.exponentialRampToValueAtTime(180, t + 0.35);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.24 * power, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
    src.connect(lp).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + 0.5);

    // Низкий удар в грудь — без него взрыв звучит как шипение.
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.exponentialRampToValueAtTime(38, t + 0.3);
    const og = this.ctx.createGain();
    og.gain.setValueAtTime(0.0001, t);
    og.gain.exponentialRampToValueAtTime(0.3 * power, t + 0.015);
    og.gain.exponentialRampToValueAtTime(0.0001, t + 0.34);
    osc.connect(og).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.36);
  }

  /** Пуск оружия за донат: восходящий свист. */
  launch(): void {
    if (!this.ensure() || !this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(180, t);
    osc.frequency.exponentialRampToValueAtTime(1400, t + 0.28);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.14, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.34);
  }

  /** Повышение уровня: две ноты вверх. */
  levelUp(): void {
    if (!this.ensure() || !this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    for (const [i, f] of [660, 990].entries()) {
      const osc = this.ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const g = this.ctx.createGain();
      const at = t + i * 0.08;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.16, at + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.22);
      osc.connect(g).connect(this.master);
      osc.start(at);
      osc.stop(at + 0.24);
    }
  }
}
